import AppBarStandard from '@/Elements/AppBarStandard';
import MainContainer from '@/Elements/MainContainer';
import Navbar from '@/Elements/Navbar';
import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import autoAnimate from '@formkit/auto-animate';
import { toast, Bounce } from 'react-toastify';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Label } from '@/components/ui/label';
import { PhoneIcon, UserIcon, EnvelopeIcon, BuildingOfficeIcon } from '@heroicons/react/24/solid';
import { Textarea } from '@/components/ui/textarea';
import { Check, Loader } from 'lucide-react';
import ChildrenDataTable from '@/Elements/ChildrenDataTable'; // Import the new component
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useAuth } from '@/Services/AuthContext';
import Arc from '@/lib/Arc';

const GuardianProfileForm = ({ onSubmit, initialData, formRef, saving }) => {
    const parent = useRef(null);

    useEffect(() => {
        parent.current && autoAnimate(parent.current);
    }, [parent]);

    const guardianSchema = Yup.object().shape({
        full_name: Yup.string().required('Full Name is required'),
        email: Yup.string().email('Invalid email address').nullable(),
        address: Yup.string().nullable(),
        pincode: Yup.string().matches(/^[0-9]{6}$/, 'Pincode must be 6 digits').nullable(),
        emergency_contact: Yup.string().matches(/^[0-9]{10}$/, 'Emergency contact must be 10 digits').nullable(),
    });

    const initialValues = initialData || {
        full_name: '',
        mobile_number: '',
        email: '',
        address: '',
        pincode: '',
        emergency_contact: '',
    };

    return (
        <div className="mt-4">
            {/* <h4 className="text-lg font-bold mb-4">Basic Information</h4> */}
            <Formik
                initialValues={initialValues}
                validationSchema={guardianSchema}
                onSubmit={onSubmit}
                enableReinitialize
                innerRef={formRef} // Pass the ref to Formik
            >
                {({ errors, touched, isSubmitting, handleChange, values }) => (
                    <Form className="space-y-4 md:grid md:grid-cols-2 md:gap-4" ref={parent}>
                        <div class="md:mt-4">
                            <Label htmlFor="full_name" className="mb-1">Full Name</Label>
                            <InputGroup>
                                <InputGroupInput id="full_name" name="full_name" onChange={handleChange} value={values.full_name} placeholder="Your Full Name" />
                                <InputGroupAddon><UserIcon className="h-4 w-4" /></InputGroupAddon>
                            </InputGroup>
                            {errors.full_name && touched.full_name && <div className="text-red-500 text-sm mt-1">{errors.full_name}</div>}
                        </div>
                        <div>
                            <Label htmlFor="pincode" className="mb-1">Pincode</Label>
                            <InputGroup>
                                <InputGroupInput id="pincode" name="pincode" onChange={handleChange} value={values.pincode} placeholder="Your Pincode" />
                                <InputGroupAddon><BuildingOfficeIcon className="h-4 w-4" /></InputGroupAddon>
                            </InputGroup>
                            {errors.pincode && touched.pincode && <div className="text-red-500 text-sm mt-1">{errors.pincode}</div>}
                        </div>
                        <div>
                            <Label htmlFor="email" className="mb-1">Email</Label>
                            <InputGroup>
                                <InputGroupInput id="email" name="email" type="email" onChange={handleChange} value={values.email} placeholder="Your Email" />
                                <InputGroupAddon><EnvelopeIcon className="h-4 w-4" /></InputGroupAddon>
                            </InputGroup>
                            {errors.email && touched.email && <div className="text-red-500 text-sm mt-1">{errors.email}</div>}
                        </div>
                        <div>
                            <Label htmlFor="emergency_contact" className="mb-1">Emergency Contact</Label>
                            <InputGroup>
                                <InputGroupInput id="emergency_contact" name="emergency_contact" onChange={handleChange} value={values.emergency_contact} placeholder="Emergency Contact Number" />
                                <InputGroupAddon><PhoneIcon className="h-4 w-4" /></InputGroupAddon>
                            </InputGroup>
                            {errors.emergency_contact && touched.emergency_contact && <div className="text-red-500 text-sm mt-1">{errors.emergency_contact}</div>}
                        </div>
                        <div className="md:col-span-2">
                            <Label htmlFor="address" className="mb-1">Address</Label>
                            <Textarea id="address" name="address" onChange={handleChange} value={values.address} placeholder="Your Address" />
                            {errors.address && touched.address && <div className="text-red-500 text-sm mt-1">{errors.address}</div>}
                        </div>

                        <div className="md:col-span-2">
                            <Button variant="ghost" type="submit" disabled={saving}>
                                <Check />
                                Save Details
                                {saving ? <Loader className='animate-spin' /> : null}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

const ProfileScreen = () => {
    const { user, isAuthenticated, loading, login, logout, invalidate } = useAuth();

    const [saving, setSaving] = useState(false);

    const [guardianData, setGuardianData] = useState(user);
    const [childrenData, setChildrenData] = useState(user.children);

    const [selectedChildren, setSelectedChildren] = useState([]);
    const guardianFormRef = useRef(null);

    const handleChildrenSelectionChange = (selectedData) => {
        setSelectedChildren(selectedData);
    };

    const saveProfile = async (profile, $form) => {
        setSaving(true)
        const cleanData = {
            emergency_contact: profile.emergency_contact,
            full_name: profile.full_name,
            address: profile.address,
            pincode: profile.pincode,
            email: profile.email,
            uid: profile.uid,
            mobile_number: profile.mobile_number,
        };
        console.log('[PROFILE] Updated Profile Data:', cleanData);


        Arc().serviceCall('guardian', 'persist', [
            [cleanData]
        ]).then(res => {
            console.log('[PROFILE] Updated Profile Data:', cleanData);
            toast.success('Profile saved successfully!');
            setSaving(false);
            // setGuardian(res.result);
            invalidate();
        }).catch(error => {
            console.error('[PROFILE] Updated Profile Data:', error);
            toast.error('Oops! Failed to save profile updates.');
            setSaving(false);
        });

    };

    return (<>
        <MainContainer>
            <Navbar></Navbar>
            <AppBarStandard options={[]
            } title={"My Profile"} description={"Manage your personal information and children's details."}>
            </AppBarStandard>

            <Tabs defaultValue="account" className="w-full mt-10">
                <TabsList>
                    <TabsTrigger value="account">Basic Details</TabsTrigger>
                    <TabsTrigger value="password">Children Management</TabsTrigger>
                </TabsList>
                <TabsContent value="account">
                    <GuardianProfileForm onSubmit={saveProfile} saving={saving} initialData={guardianData} formRef={guardianFormRef} />
                </TabsContent>
                <TabsContent value="password">
                    <ChildrenDataTable guardian={user} childrenData={childrenData} setChildrenData={setChildrenData} onSelectCallback={handleChildrenSelectionChange} onSaveCallback={() => { invalidate() }} />
                </TabsContent>
            </Tabs>
        </MainContainer>
    </>)
};
export default ProfileScreen;